import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '../../../stores/useStores';
import { formatValueWithK } from '../../../utils/helpers';
import { DEFAULT_TIMER, formatTime } from '../../../utils/time';

import styles from './ChallengesWidget.module.scss';

export const ChallengesWidget = observer(() => {
  const { challengesStore } = useStores();
  const { activeMissionProgress, activeMissionCoinType } = challengesStore;
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState<string>(DEFAULT_TIMER);
  const hasRefetchedRef = useRef(false);

  useEffect(() => {
    const expiresAt = activeMissionProgress?.expiresAt;
    if (!expiresAt) {
      setCountdown(DEFAULT_TIMER);
      hasRefetchedRef.current = false;
      return;
    }

    const updateCountdown = () => {
      const secondsLeft = dayjs(expiresAt).diff(dayjs(), 'seconds');
      if (secondsLeft < 0) {
        setCountdown(DEFAULT_TIMER);
        if (!hasRefetchedRef.current) {
          hasRefetchedRef.current = true;
          void challengesStore.fetch();
        }
        return;
      }
      hasRefetchedRef.current = false;
      setCountdown(formatTime(secondsLeft, 's', t));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [activeMissionProgress?.expiresAt, challengesStore, t]);

  const onClickHandler = () => {
    void challengesStore.startChallengesDialog(activeMissionCoinType);
  };

  const progressPercentage = activeMissionProgress
    ? Math.min((activeMissionProgress.current / activeMissionProgress.target) * 100, 100)
    : 0;

  return (
    <div className="home-widget">
      <div
        className={classNames(styles.challengesWidget, 'circle-parent')}
        data-testid="challenges-widget-button"
        onClick={onClickHandler}
      >
        <div className="circle" />
        <img
          alt="Challenges Widget"
          className={styles.widgetImage}
          src="/images/challenges/challenges-widget-main.png"
          style={{
            bottom: activeMissionProgress?.expiresAt ? '16px' : '8px',
          }}
        />
        {activeMissionProgress?.expiresAt && (
          <div className={styles.countdown} data-testid="challenges-widget-countdown">
            {countdown}
          </div>
        )}
        {activeMissionProgress && (
          <div className={styles.progressBarWrapper}>
            <div className={styles.progressBar}>
              <div className={styles.progressTrack}>
                <div className={styles.progressFill} style={{ width: `${progressPercentage}%` }} />
              </div>
              <span className={styles.progressText}>
                {formatValueWithK(activeMissionProgress.current, t)}/{formatValueWithK(activeMissionProgress.target, t)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
